import {
  mergeOnboardingModules,
} from './onboarding-modules.util';
import { resolveCargoHeatingCanonicalSegment } from './cargo-heating-segment.util';
import {
  PIPELINE_FIELD_SNAKE_MAP,
  primaryPipelineThickness,
  serializePipelineSectionsFromForm,
  sumPipelineLengths,
  sumPipelineZoneLengths,
} from './pipeline-segment-defaults.util';

export interface CargoHeatingUploadedFile {
  field: string;
  file_name: string;
  s3_url?: string;
  mime_type?: string;
  size_bytes?: number;
}

/** Backend POST / cargo-heating vessel-onboarding JSON contract (snake_case). */
export const CARGO_HEATING_ONBOARDING_API_KEYS = [
  'client_id',
  'submitted_at',
  'vessel_name',
  'imo',
  'segment',
  'vessel_type',
  'tanker_type',
  'number_of_tanks',
  'heating_arrangement',
  'steam_pipes_insulation',
  'egcs_scrubber',
  'pipeline_length_m',
  'pipeline_thickness_mm',
  'boiler_make',
  'number_of_boilers',
  'boiler_capacity',
  'max_efficiency_percent',
  'steam_production_kg_hr',
  'boiler_load_percentage',
  'deck_steam_pressure',
  'composite_boiler_consumption_per_day',
  'ae_boiler_consumption_mt',
  'deck_heat_exchanger_bunker_consumption_mt',
  'aux_boiler_fuel_consumption_kg_hr',
  'min_aux_boiler_fuel_consumption_kg_hr',
  'composite_boiler_fuel_consumption_kg_hr',
  'steam_output_oil_fired_section_kg_hr',
  'steam_output_exhaust_section_me_motor_kg_hr',
  'steam_output_exhaust_section_ae_motor_kg_hr',
  'deck_heater_capacity_kw',
  'egb_capacity_kg_hr',
  'egb_exhaust_gas_quantity_kg_hr',
  'egb_gas_temp_inlet_deg_c',
  'egb_gas_temp_outlet_deg_c',
  'pipeline_main_line_thickness_mm',
  'pipeline_branch_line_thickness_mm',
  'pipeline_in_engine_room_thickness_mm',
  'pipeline_main_line_length_m',
  'pipeline_branch_line_length_m',
  'pipeline_in_engine_room_length_m',
  'steam_insulation_main_line',
  'steam_insulation_branch_line',
  'steam_insulation_in_engine_room',
  'pipeline_sections',
  'boiler_graph_applied',
  'boiler_graph_file_name',
  'steam_table_applied',
  'steam_table_file_name',
  'liquid_cargo_applied',
  'liquid_cargo_file_name',
  'liquid_cargo_fill_note',
  'liquid_cargo_totals',
  'tanks',
  'liquid_cargo',
  'uploaded_files',
  'onboarding_modules',
  'quick_onboarding',
  'boiler_efficiency_curve',
] as const;

const JSON_ARRAY_OBJECT_KEYS = new Set([
  'tanks',
  'liquid_cargo',
  'liquid_cargo_totals',
  'pipeline_sections',
  'uploaded_files',
  'onboarding_modules',
  'boiler_efficiency_curve',
]);

export function toOptionalNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const cleaned = String(value).replace(/,/g, '').replace(/[^\d.-]/g, '');
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

function pickString(...sources: unknown[]): string | null {
  for (const source of sources) {
    if (source === undefined || source === null) {
      continue;
    }
    const text = String(source).trim();
    if (text !== '') {
      return text;
    }
  }
  return null;
}

function pickNumber(...sources: unknown[]): number | null {
  for (const source of sources) {
    const parsed = toOptionalNumber(source);
    if (parsed !== null) {
      return parsed;
    }
  }
  return null;
}

function pickBoolean(...sources: unknown[]): boolean | null {
  for (const source of sources) {
    if (source === undefined || source === null) {
      continue;
    }
    if (typeof source === 'boolean') {
      return source;
    }
    if (source === 'true' || source === 'Yes' || source === 'yes' || source === 1) {
      return true;
    }
    if (source === 'false' || source === 'No' || source === 'no' || source === 0) {
      return false;
    }
  }
  return null;
}

function parseJsonField<T>(value: unknown): T | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    try {
      return JSON.parse(trimmed) as T;
    } catch {
      return null;
    }
  }
  return value as T;
}

export function normalizeUploadedFilesMetadata(
  entries: unknown,
): CargoHeatingUploadedFile[] {
  if (!Array.isArray(entries)) {
    return [];
  }

  return entries
    .map((entry) => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }
      const file = entry as Record<string, unknown>;
      const field = String(file['field'] ?? '').trim();
      const file_name = String(file['file_name'] ?? file['fileName'] ?? '').trim();
      if (!field || !file_name) {
        return null;
      }
      const normalized: CargoHeatingUploadedFile = { field, file_name };
      const s3Url = String(file['s3_url'] ?? file['s3Url'] ?? '').trim();
      if (s3Url) {
        normalized.s3_url = s3Url;
      }
      const mimeType = String(file['mime_type'] ?? file['mimeType'] ?? '').trim();
      if (mimeType) {
        normalized.mime_type = mimeType;
      }
      const sizeBytes = toOptionalNumber(file['size_bytes'] ?? file['sizeBytes']);
      if (sizeBytes !== null) {
        normalized.size_bytes = sizeBytes;
      }
      return normalized;
    })
    .filter((entry): entry is CargoHeatingUploadedFile => !!entry);
}

export function mergeUploadedFilesMetadata(
  existing: CargoHeatingUploadedFile[],
  updates: CargoHeatingUploadedFile[],
): CargoHeatingUploadedFile[] {
  const byField = new Map<string, CargoHeatingUploadedFile>();
  for (const file of existing) {
    if (file.field) {
      byField.set(file.field, { ...file });
    }
  }
  for (const file of updates) {
    if (!file.field) {
      continue;
    }
    const previous = byField.get(file.field);
    byField.set(file.field, {
      field: file.field,
      file_name: file.file_name,
      s3_url: file.s3_url ?? previous?.s3_url,
      mime_type: file.mime_type ?? previous?.mime_type,
      size_bytes: file.size_bytes ?? previous?.size_bytes,
    });
  }
  return Array.from(byField.values());
}

/** Normalize a GET vessel-onboarding row for merge-on-update. */
export function normalizeCargoHeatingOnboardingApiRow(
  row: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  if (!row) {
    return {};
  }

  const normalized: Record<string, unknown> = { ...row };
  JSON_ARRAY_OBJECT_KEYS.forEach((key) => {
    const parsed = parseJsonField(normalized[key]);
    if (parsed !== null) {
      normalized[key] = parsed;
    }
  });

  if (Array.isArray(normalized['uploaded_files'])) {
    normalized['uploaded_files'] = normalizeUploadedFilesMetadata(
      normalized['uploaded_files'],
    );
  }

  return normalized;
}

export function finalizeCargoHeatingOnboardingPayload(
  payload: Record<string, unknown>,
  original?: Record<string, unknown> | null,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  CARGO_HEATING_ONBOARDING_API_KEYS.forEach((key) => {
    const value =
      payload[key] !== undefined ? payload[key] : original?.[key];
    if (value !== undefined) {
      result[key] = value;
    }
  });

  result['onboarding_modules'] = mergeOnboardingModules(
    original?.['onboarding_modules'],
    payload['onboarding_modules'],
    'cargo-heating',
  );

  return result;
}

/** Build POST body from onboarded vessel detail row, merging with the previous API snapshot. */
export function buildCargoHeatingOnboardingPayloadFromDetail(
  detail: Record<string, unknown>,
  original?: Record<string, unknown> | null,
): Record<string, unknown> {
  const normalizedOriginal = normalizeCargoHeatingOnboardingApiRow(
    original ?? detail,
  );
  const normalizedDetail = normalizeCargoHeatingOnboardingApiRow(detail);
  const finalized = finalizeCargoHeatingOnboardingPayload(
    normalizedDetail,
    normalizedOriginal,
  );
  assertNoCargoHeatingPayloadDataLoss(normalizedOriginal, finalized);
  return finalized;
}

export function assertNoCargoHeatingPayloadDataLoss(
  original: Record<string, unknown>,
  payload: Record<string, unknown>,
): void {
  if (typeof console === 'undefined' || !console.warn) {
    return;
  }

  const missing: string[] = [];
  Object.keys(original).forEach((key) => {
    if (key === 'onboarding_status' || key === 'id' || key === 'updated_at') {
      return;
    }
    const originalValue = original[key];
    if (originalValue === undefined || originalValue === null) {
      return;
    }
    if (!(key in payload) && CARGO_HEATING_ONBOARDING_API_KEYS.includes(key as never)) {
      missing.push(key);
    }
  });

  if (missing.length) {
    console.warn(
      '[cargo-heating] onboarding payload may have lost keys:',
      missing,
    );
  }
}

function pipelineFlatValue(
  formValue: Record<string, unknown>,
  formKey: string,
  snakeKey: string,
  original: Record<string, unknown>,
): number | null {
  return (
    pickNumber(formValue[formKey], original[snakeKey]) ??
    pickNumber(original[snakeKey])
  );
}

export interface BuildCargoHeatingOnboardingPayloadInput {
  clientId: number;
  submittedAt: string;
  formValue: Record<string, unknown>;
  tanks: Array<Record<string, unknown>>;
  liquidCargo: Array<Record<string, unknown>>;
  liquidCargoTotals: Record<string, unknown> | null;
  boilerGraphApplied: boolean;
  boilerGraphFileName: string;
  steamTableApplied: boolean;
  steamTableFileName: string;
  liquidCargoApplied: boolean;
  liquidCargoFileName: string;
  liquidCargoFillNote: string;
  uploadedFiles: CargoHeatingUploadedFile[];
  steamPipesInsulation: string;
  boilerEfficiencyCurve?: Record<string, unknown> | null;
  onboardingModules?: string[] | null;
  quickOnboarding?: boolean | null;
  original?: Record<string, unknown> | null;
}

export function buildCargoHeatingOnboardingPayload(
  input: BuildCargoHeatingOnboardingPayloadInput,
): Record<string, unknown> {
  const original = input.original ?? {};
  const formValue = input.formValue;

  const pipelineSections =
    input.formValue && Object.keys(formValue).length
      ? serializePipelineSectionsFromForm(formValue)
      : parseJsonField<Array<Record<string, unknown>>>(original['pipeline_sections']) ??
        [];

  const payload: Record<string, unknown> = {
    client_id: input.clientId,
    submitted_at:
      pickString(original['submitted_at'], input.submittedAt) ??
      new Date().toISOString(),
    vessel_name:
      pickString(formValue['vesselName'], formValue['vessel_name'], original['vessel_name']) ??
      '',
    imo:
      pickString(formValue['imo'], original['imo'])?.replace(/\D/g, '') ?? '',
    segment:
      resolveCargoHeatingCanonicalSegment(
        formValue,
        pickString(formValue['segment'], original['segment']) ?? '',
      ) ?? '',
    vessel_type:
      pickString(formValue['vesselType'], formValue['vessel_type'], original['vessel_type']) ??
      '',
    tanker_type:
      pickString(formValue['tankerType'], formValue['tanker_type'], original['tanker_type']) ??
      '',
    number_of_tanks:
      pickNumber(formValue['numberOfTanks'], formValue['number_of_tanks'], original['number_of_tanks']) ??
      (Array.isArray(input.tanks) ? input.tanks.length : null),
    heating_arrangement:
      pickString(
        formValue['heatingArrangement'],
        formValue['heating_arrangement'],
        original['heating_arrangement'],
      ) ?? '',
    steam_pipes_insulation:
      pickString(
        input.steamPipesInsulation,
        formValue['steamPipesInsulation'],
        formValue['steam_pipes_insulation'],
        original['steam_pipes_insulation'],
      ) ?? 'Yes',
    egcs_scrubber:
      pickString(
        formValue['egcsScrubber'],
        formValue['egcs_scrubber'],
        original['egcs_scrubber'],
      ) ?? 'No',
    pipeline_length_m:
      sumPipelineLengths(formValue) ??
      pickNumber(original['pipeline_length_m']),
    pipeline_thickness_mm:
      pickNumber(primaryPipelineThickness(formValue), original['pipeline_thickness_mm']),
    boiler_make:
      pickString(formValue['boilerMake'], formValue['boiler_make'], original['boiler_make']) ??
      '',
    number_of_boilers: pickNumber(
      formValue['numberOfBoilers'],
      formValue['number_of_boilers'],
      original['number_of_boilers'],
    ),
    boiler_capacity: pickNumber(
      formValue['boilerCapacity'],
      formValue['boiler_capacity'],
      original['boiler_capacity'],
    ),
    max_efficiency_percent: pickNumber(
      formValue['maxEfficiencyPercent'],
      formValue['max_efficiency_percent'],
      original['max_efficiency_percent'],
    ),
    steam_production_kg_hr: pickNumber(
      formValue['steamProductionKgHr'],
      formValue['steam_production_kg_hr'],
      original['steam_production_kg_hr'],
    ),
    boiler_load_percentage: pickNumber(
      formValue['boilerLoadPercentage'],
      formValue['boiler_load_percentage'],
      original['boiler_load_percentage'],
    ),
    deck_steam_pressure: pickNumber(
      formValue['deckSteamPressure'],
      formValue['deck_steam_pressure'],
      original['deck_steam_pressure'],
    ),
    composite_boiler_consumption_per_day: pickNumber(
      formValue['compositeBoilerConsumptionPerDay'],
      formValue['composite_boiler_consumption_per_day'],
      original['composite_boiler_consumption_per_day'],
    ),
    ae_boiler_consumption_mt: pickNumber(
      formValue['aeBoilerConsumptionMt'],
      formValue['ae_boiler_consumption_mt'],
      original['ae_boiler_consumption_mt'],
    ),
    deck_heat_exchanger_bunker_consumption_mt: pickNumber(
      formValue['deckHeatExchangerBunkerConsumptionMt'],
      formValue['deck_heat_exchanger_bunker_consumption_mt'],
      original['deck_heat_exchanger_bunker_consumption_mt'],
    ),
    aux_boiler_fuel_consumption_kg_hr: pickNumber(
      formValue['auxBoilerFuelConsumptionKgHr'],
      formValue['aux_boiler_fuel_consumption_kg_hr'],
      original['aux_boiler_fuel_consumption_kg_hr'],
    ),
    min_aux_boiler_fuel_consumption_kg_hr: pickNumber(
      formValue['minAuxBoilerFuelConsumption'],
      formValue['min_aux_boiler_fuel_consumption_kg_hr'],
      original['min_aux_boiler_fuel_consumption_kg_hr'],
    ),
    composite_boiler_fuel_consumption_kg_hr: pickNumber(
      formValue['compositeBoilerFuelConsumptionKgHr'],
      formValue['composite_boiler_fuel_consumption_kg_hr'],
      original['composite_boiler_fuel_consumption_kg_hr'],
    ),
    steam_output_oil_fired_section_kg_hr: pickNumber(
      formValue['steamOutputOilFiredSectionKgHr'],
      formValue['steam_output_oil_fired_section_kg_hr'],
      original['steam_output_oil_fired_section_kg_hr'],
    ),
    steam_output_exhaust_section_me_motor_kg_hr: pickNumber(
      formValue['steamOutputExhaustSectionMeMotorKgHr'],
      formValue['steam_output_exhaust_section_me_motor_kg_hr'],
      original['steam_output_exhaust_section_me_motor_kg_hr'],
    ),
    steam_output_exhaust_section_ae_motor_kg_hr: pickNumber(
      formValue['steamOutputExhaustSectionAeMotorKgHr'],
      formValue['steam_output_exhaust_section_ae_motor_kg_hr'],
      original['steam_output_exhaust_section_ae_motor_kg_hr'],
    ),
    deck_heater_capacity_kw: pickNumber(
      formValue['deckHeaterCapacityKw'],
      formValue['deck_heater_capacity_kw'],
      original['deck_heater_capacity_kw'],
    ),
    egb_capacity_kg_hr: pickNumber(
      formValue['egbCapacityKgHr'],
      formValue['egb_capacity_kg_hr'],
      original['egb_capacity_kg_hr'],
    ),
    egb_exhaust_gas_quantity_kg_hr: pickNumber(
      formValue['egbExhaustGasQuantityKgHr'],
      formValue['egb_exhaust_gas_quantity_kg_hr'],
      original['egb_exhaust_gas_quantity_kg_hr'],
    ),
    egb_gas_temp_inlet_deg_c: pickNumber(
      formValue['egbGasTempInletDegC'],
      formValue['egb_gas_temp_inlet_deg_c'],
      original['egb_gas_temp_inlet_deg_c'],
    ),
    egb_gas_temp_outlet_deg_c: pickNumber(
      formValue['egbGasTempOutletDegC'],
      formValue['egb_gas_temp_outlet_deg_c'],
      original['egb_gas_temp_outlet_deg_c'],
    ),
    pipeline_main_line_thickness_mm: pipelineFlatValue(
      formValue,
      'pipelineMainLineThickness1Mm',
      'pipeline_main_line_thickness_mm',
      original,
    ),
    pipeline_branch_line_thickness_mm: pipelineFlatValue(
      formValue,
      'pipelineBranchLineThickness1Mm',
      'pipeline_branch_line_thickness_mm',
      original,
    ),
    pipeline_in_engine_room_thickness_mm: pipelineFlatValue(
      formValue,
      'pipelineInEngineRoomThicknessMm',
      'pipeline_in_engine_room_thickness_mm',
      original,
    ),
    pipeline_main_line_length_m:
      sumPipelineZoneLengths(formValue, 'mainLine') ??
      pickNumber(original['pipeline_main_line_length_m']),
    pipeline_branch_line_length_m:
      sumPipelineZoneLengths(formValue, 'branchLine') ??
      pickNumber(original['pipeline_branch_line_length_m']),
    pipeline_in_engine_room_length_m:
      sumPipelineZoneLengths(formValue, 'inEngineRoom') ??
      pickNumber(original['pipeline_in_engine_room_length_m']),
    steam_insulation_main_line:
      pickString(
        formValue['steamInsulationMainLine'],
        formValue['steam_insulation_main_line'],
        original['steam_insulation_main_line'],
      ) ?? 'Yes',
    steam_insulation_branch_line:
      pickString(
        formValue['steamInsulationBranchLine'],
        formValue['steam_insulation_branch_line'],
        original['steam_insulation_branch_line'],
      ) ?? 'Yes',
    steam_insulation_in_engine_room:
      pickString(
        formValue['steamInsulationInEngineRoom'],
        formValue['steam_insulation_in_engine_room'],
        original['steam_insulation_in_engine_room'],
      ) ?? 'Yes',
    pipeline_sections:
      pipelineSections.length > 0
        ? pipelineSections
        : parseJsonField(original['pipeline_sections']) ?? [],
    boiler_graph_applied:
      pickBoolean(input.boilerGraphApplied, original['boiler_graph_applied']) ??
      false,
    boiler_graph_file_name:
      pickString(
        input.boilerGraphFileName,
        original['boiler_graph_file_name'],
      ) ?? '',
    steam_table_applied:
      pickBoolean(input.steamTableApplied, original['steam_table_applied']) ??
      false,
    steam_table_file_name:
      pickString(input.steamTableFileName, original['steam_table_file_name']) ??
      '',
    liquid_cargo_applied:
      pickBoolean(input.liquidCargoApplied, original['liquid_cargo_applied']) ??
      false,
    liquid_cargo_file_name:
      pickString(input.liquidCargoFileName, original['liquid_cargo_file_name']) ??
      '',
    liquid_cargo_fill_note:
      pickString(input.liquidCargoFillNote, original['liquid_cargo_fill_note']) ??
      '',
    liquid_cargo_totals:
      input.liquidCargoTotals ??
      parseJsonField(original['liquid_cargo_totals']),
    tanks: input.tanks.length
      ? input.tanks
      : parseJsonField(original['tanks']) ?? [],
    liquid_cargo: input.liquidCargo.length
      ? input.liquidCargo
      : parseJsonField(original['liquid_cargo']) ?? [],
    boiler_efficiency_curve:
      input.boilerEfficiencyCurve ??
      parseJsonField(original['boiler_efficiency_curve']),
    uploaded_files: input.uploadedFiles,
    onboarding_modules: mergeOnboardingModules(
      input.onboardingModules,
      original['onboarding_modules'],
      'cargo-heating',
    ),
    quick_onboarding:
      pickBoolean(input.quickOnboarding, original['quick_onboarding']) ?? false,
  };

  Object.entries(PIPELINE_FIELD_SNAKE_MAP).forEach(([formKey, snakeKey]) => {
    if (snakeKey in payload) {
      return;
    }
    const value = pickNumber(formValue[formKey], original[snakeKey]);
    if (value !== null) {
      payload[snakeKey] = value;
    }
  });

  const finalized = finalizeCargoHeatingOnboardingPayload(payload, original);
  assertNoCargoHeatingPayloadDataLoss(original, finalized);
  return finalized;
}
